import { bezSierotek } from '@insolitum/ui';
import {
  Eye,
  EyeOff,
  UserPlus,
  AlertCircle,
  Check,
  Building2,
  MapPin,
  Phone,
  Mail,
  User,
  FileText,
  Clock,
  Search,
  Loader2,
} from 'lucide-react';
import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';

import {
  IKONA_OSTRZEZENIA,
  IKONA_SUKCESU,
  LINK_PARTNERA,
  POLE_WEJSCIA,
  PoleWyboruWejscia,
  TEKST_PARTNERA,
  UkladWejscia,
} from '../../components/marka/UkladWejscia';
import { POLISH_REGIONS } from '../../hooks/usePartners';
import { supabase } from '../../lib/supabase';
import { fetchCompanyByNip, validateNip } from '../../lib/vat-api';

interface PartnerFormData {
  // User data
  fullName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  // Company data
  companyName: string;
  nip: string;
  address: string;
  city: string;
  postalCode: string;
  // VAT API data
  voivodeship: string;
  county: string;
  regon: string;
  krs: string;
  vatStatus: string;
  // Preferences
  preferredRegions: string[];
  experience: string;
  notes: string;
}

export function PartnerRegisterPage() {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [nipFetching, setNipFetching] = useState(false);
  const [nipFetched, setNipFetched] = useState(false);
  const [nipError, setNipError] = useState<string | null>(null);

  const [formData, setFormData] = useState<PartnerFormData>({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    nip: '',
    address: '',
    city: '',
    postalCode: '',
    voivodeship: '',
    county: '',
    regon: '',
    krs: '',
    vatStatus: '',
    preferredRegions: [],
    experience: '',
    notes: '',
  });

  const updateField = (field: keyof PartnerFormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Reset NIP fetch status when NIP changes
    if (field === 'nip') {
      setNipFetched(false);
      setNipError(null);
    }
  };

  const toggleRegion = (code: string) => {
    setFormData((prev) => ({
      ...prev,
      preferredRegions: prev.preferredRegions.includes(code)
        ? prev.preferredRegions.filter((r) => r !== code)
        : [...prev.preferredRegions, code],
    }));
  };

  // Auto-fetch company data from VAT API by NIP
  const handleNipFetch = useCallback(async () => {
    const cleanNip = formData.nip.replace(/\D/g, '');

    if (cleanNip.length !== 10) {
      setNipError('NIP musi mieć 10 cyfr');
      return;
    }

    if (!validateNip(cleanNip)) {
      setNipError('Nieprawidłowa suma kontrolna NIP');
      return;
    }

    setNipFetching(true);
    setNipError(null);

    try {
      const companyData = await fetchCompanyByNip(cleanNip);

      setFormData((prev) => ({
        ...prev,
        companyName: companyData.companyName || prev.companyName,
        address: companyData.address || prev.address,
        city: companyData.city || prev.city,
        postalCode: companyData.postalCode || prev.postalCode,
        voivodeship: companyData.voivodeship || prev.voivodeship,
        county: companyData.county || prev.county,
        regon: companyData.regon || '',
        krs: companyData.krs || '',
        vatStatus: companyData.vatStatus || '',
      }));
      setNipFetched(true);
    } catch (err: any) {
      console.error('NIP fetch error:', err);
      setNipError(err.message || 'Nie udało się pobrać danych z rejestru VAT');
    } finally {
      setNipFetching(false);
    }
  }, [formData.nip]);

  const validateStep1 = () => {
    if (!formData.fullName || !formData.email || !formData.phone) {
      setError('Wypełnij wszystkie wymagane pola');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Hasło musi mieć minimum 8 znaków');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Hasła nie są identyczne');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.companyName || !formData.nip || !formData.city) {
      setError('Wypełnij wszystkie wymagane pola firmowe');
      return false;
    }
    const cleanNip = formData.nip.replace(/\D/g, '');
    if (cleanNip.length !== 10) {
      setError('NIP musi mieć 10 cyfr');
      return false;
    }
    if (!validateNip(cleanNip)) {
      setError('Nieprawidłowy NIP - błędna suma kontrolna');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    setError(null);
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.preferredRegions.length === 0) {
      setError('Wybierz przynajmniej jedno województwo');
      return;
    }

    setIsLoading(true);

    try {
      // Create user account with all partner data in metadata.
      // The database trigger (handle_new_partner_registration) will automatically
      // create the organization, partner record, and user_organizations entry.
      const { error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            phone: formData.phone,
            role: 'partner_pending',
            // Company data for the trigger to create organization
            company_name: formData.companyName,
            nip: formData.nip.replace(/\D/g, ''),
            address: formData.address || null,
            city: formData.city || null,
            postal_code: formData.postalCode || null,
            voivodeship: formData.voivodeship || null,
            county: formData.county || null,
            regions: formData.preferredRegions,
            notes: formData.experience
              ? `Doświadczenie: ${formData.experience}\n\n${formData.notes || ''}`
              : formData.notes || null,
          },
          emailRedirectTo: `${window.location.origin}/login`,
        },
      });

      if (authError) throw authError;

      setSuccess(true);
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'Wystąpił błąd podczas rejestracji');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <UkladWejscia>
        <div className="card rounded-ids-2xl shadow-elev-4 p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
            <Mail className={`w-8 h-8 ${IKONA_SUKCESU}`} />
          </div>
          <h1 className="ids-tytul-ekranu">Sprawdź swoją skrzynkę email!</h1>
          <p className="ids-tresc-ekranu mx-auto">
            Na adres <strong className="text-aurora-text">{formData.email}</strong> wysłaliśmy link
            potwierdzający rejestrację.
          </p>
          <div className="bg-aurora-bg-subtle border border-aurora-border rounded-ids-lg p-4 mt-6 text-left">
            <p className="text-sm text-aurora-text mb-2">Co dalej:</p>
            <ul className="text-sm text-aurora-muted space-y-2">
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-aurora-brand mt-0.5 flex-shrink-0" />
                {bezSierotek('Kliknij w link w wiadomości email, aby potwierdzić konto')}
              </li>
              <li className="flex items-start gap-2">
                <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${IKONA_SUKCESU}`} />
                Twoja organizacja została automatycznie utworzona na podstawie NIP
              </li>
              <li className="flex items-start gap-2">
                <Clock className={`w-4 h-4 mt-0.5 flex-shrink-0 ${IKONA_OSTRZEZENIA}`} />
                {bezSierotek(
                  'Po potwierdzeniu email zaloguj się — nasz zespół zweryfikuje Twoje zgłoszenie'
                )}
              </li>
            </ul>
          </div>
          <Link to="/login" className="btn btn--primary btn--lg mt-8">
            Przejdź do logowania
          </Link>
        </div>
      </UkladWejscia>
    );
  }

  // Rejestracja partnera należy do rodziny ekranów wejścia: znak i przełącznik
  // języka stoją tam, gdzie na logowaniu i zwykłej rejestracji (link
  // „Zarejestruj się” na domenie partnera nie przesuwa znaku). Kolumna jest
  // szeroka, bo formularz ma pola po dwa w wierszu. Kafel z ikoną budynku
  // stał tu na osi znaku jako druga, obca postać ekranu, więc go nie ma.
  return (
    <UkladWejscia szeroka>
      {/* Header */}
      <div className="text-center">
        <h1 className="ids-tytul-ekranu">Zostań partnerem</h1>
        <p className="ids-tresc-ekranu mx-auto">
          Dołącz do programu partnerskiego Insolitum Universe
        </p>
      </div>

      {/* Kroki — jeden wiersz w osi na każdej szerokości. Do 05.10.2026
          `flex-wrap` łamał je na telefonie na dwa wiersze (krok 1 sam,
          z wiszącą kreską, −21 px od osi). Poniżej 640 px widać etykietę
          tylko bieżącego kroku (pozostałe zostają dla czytnika), a kreski są
          krótsze; od 640 px wszystkie etykiety. Cyfra w bursztynie jest
          atramentem (biel na amber-500 = 2,15:1). */}
      <ol
        aria-label="Kroki rejestracji"
        className="flex items-center justify-center gap-2 my-8 sm:gap-4"
      >
        {[1, 2, 3].map((s) => (
          <li
            key={s}
            aria-current={step === s ? 'step' : undefined}
            className="flex items-center gap-2"
          >
            <div
              className={`mono w-8 h-8 shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${
                step >= s
                  ? 'bg-amber-500 text-cosmos-950 shadow-glow'
                  : 'bg-aurora-bg-subtle text-aurora-subtle border border-aurora-border'
              }`}
            >
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            <span
              className={`whitespace-nowrap text-sm font-medium ${
                step >= s ? 'text-aurora-text' : 'text-aurora-subtle'
              } ${step === s ? '' : 'max-sm:sr-only'}`}
            >
              {s === 1 && 'Dane osobowe'}
              {s === 2 && 'Dane firmy'}
              {s === 3 && 'Regiony'}
            </span>
            {s < 3 && (
              <div
                aria-hidden="true"
                className={`w-6 h-0.5 shrink-0 rounded-full sm:w-8 ${step > s ? 'bg-amber-500' : 'bg-aurora-border'}`}
              />
            )}
          </li>
        ))}
      </ol>

      {/* Form */}
      <div className="card rounded-ids-2xl shadow-elev-4 p-4 sm:p-8">
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="flex items-center gap-2 p-3 mb-6 bg-red-500/10 border border-red-500/20 rounded-ids-md text-red-400 [[data-mode=light]_&]:text-red-800 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {bezSierotek(error)}
            </div>
          )}

          {/* Step 1: Personal Data */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="field">
                <label className="field__label">
                  <User className="w-4 h-4 inline mr-2" />
                  {bezSierotek('Imię i nazwisko *')}
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => updateField('fullName', e.target.value)}
                  required
                  className={`input ${POLE_WEJSCIA}`}
                  placeholder="Jan Kowalski"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="field">
                  <label className="field__label">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    required
                    className={`input ${POLE_WEJSCIA}`}
                    placeholder="jan@firma.pl"
                  />
                </div>
                <div className="field">
                  <label className="field__label">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    required
                    className={`input ${POLE_WEJSCIA}`}
                    placeholder="+48 123 456 789"
                  />
                </div>
              </div>

              <div className="field">
                <label className="field__label">Hasło *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    required
                    autoComplete="new-password"
                    className={`input pr-12 ${POLE_WEJSCIA}`}
                    placeholder="Minimum 8 znaków"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-aurora-subtle hover:text-aurora-text transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <div className="field">
                <label className="field__label">Potwierdź hasło *</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateField('confirmPassword', e.target.value)}
                  required
                  autoComplete="new-password"
                  className={`input ${POLE_WEJSCIA}`}
                  placeholder="Powtórz hasło"
                />
              </div>
            </div>
          )}

          {/* Step 2: Company Data */}
          {step === 2 && (
            <div className="space-y-5">
              <div className="field">
                <label className="field__label">
                  <FileText className="w-4 h-4 inline mr-2" />
                  NIP *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.nip}
                    onChange={(e) => updateField('nip', e.target.value.replace(/\D/g, ''))}
                    required
                    maxLength={10}
                    className={`input mono flex-1 ${POLE_WEJSCIA}`}
                    placeholder="1234567890"
                  />
                  <button
                    type="button"
                    onClick={handleNipFetch}
                    disabled={nipFetching || formData.nip.replace(/\D/g, '').length !== 10}
                    className={`btn border-amber-500/30 bg-amber-500/20 hover:bg-amber-500/30 ${TEKST_PARTNERA}`}
                    title="Pobierz dane firmy z rejestru VAT"
                  >
                    {nipFetching ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Search className="w-5 h-5" />
                    )}
                    <span className="hidden sm:inline">Pobierz dane</span>
                  </button>
                </div>
                {nipError && <p className="field__err">{bezSierotek(nipError)}</p>}
                {nipFetched && (
                  <p className="text-sm text-green-400 [[data-mode=light]_&]:text-green-800 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    {bezSierotek('Dane pobrane z rejestru VAT')}
                  </p>
                )}
              </div>

              <div className="field">
                <label className="field__label">
                  <Building2 className="w-4 h-4 inline mr-2" />
                  Nazwa firmy *
                </label>
                <input
                  type="text"
                  value={formData.companyName}
                  onChange={(e) => updateField('companyName', e.target.value)}
                  required
                  className={`input ${POLE_WEJSCIA}`}
                  placeholder="Nazwa Sp. z o.o."
                />
              </div>

              <div className="field">
                <label className="field__label">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Adres siedziby
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  className={`input ${POLE_WEJSCIA}`}
                  placeholder="ul. Przykładowa 1"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="field">
                  <label className="field__label">Miasto *</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    required
                    className={`input ${POLE_WEJSCIA}`}
                    placeholder="Warszawa"
                  />
                </div>
                <div className="field">
                  <label className="field__label">Kod pocztowy</label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => updateField('postalCode', e.target.value)}
                    className={`input mono ${POLE_WEJSCIA}`}
                    placeholder="00-000"
                  />
                </div>
              </div>

              <div className="field">
                <label className="field__label">{bezSierotek('Doświadczenie w branży')}</label>
                <textarea
                  value={formData.experience}
                  onChange={(e) => updateField('experience', e.target.value)}
                  rows={3}
                  className={`textarea resize-none ${POLE_WEJSCIA}`}
                  placeholder="Opisz swoje doświadczenie w branży IT/IoT..."
                />
              </div>
            </div>
          )}

          {/* Step 3: Regions */}
          {step === 3 && (
            <div className="space-y-5">
              <div>
                <label className="field__label block mb-4">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  {bezSierotek('Wybierz województwa, w których chcesz działać *')}
                </label>
                <p className="text-sm text-aurora-subtle mb-4">
                  Ostateczne przypisanie regionów zostanie ustalone przez administratora.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {POLISH_REGIONS.map((region) => (
                    <label
                      key={region.code}
                      className={`flex items-center gap-2 p-3 rounded-ids-md cursor-pointer transition-colors border ${
                        formData.preferredRegions.includes(region.code)
                          ? 'bg-amber-500/20 border-amber-500'
                          : 'bg-aurora-surface border-aurora-border hover:bg-aurora-bg-subtle'
                      }`}
                    >
                      <PoleWyboruWejscia
                        akcent="partner"
                        checked={formData.preferredRegions.includes(region.code)}
                        onChange={() => toggleRegion(region.code)}
                      />
                      <span className="text-sm text-aurora-text capitalize">{region.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="field">
                <label className="field__label">Dodatkowe uwagi</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  rows={3}
                  className={`textarea resize-none ${POLE_WEJSCIA}`}
                  placeholder="Dodatkowe informacje, pytania..."
                />
              </div>

              <div className="flex items-start">
                <PoleWyboruWejscia akcent="partner" id="terms" required className="mt-0.5" />
                <label htmlFor="terms" className="ml-2 text-sm text-aurora-muted">
                  Akceptuję{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK_PARTNERA}
                  >
                    Regulamin
                  </a>{' '}
                  oraz{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={LINK_PARTNERA}
                  >
                    Politykę Prywatności
                  </a>
                </label>
              </div>
            </div>
          )}

          {/* Nawigacja. Akcja główna to `btn--primary` rodziny wejścia — ta sama,
              co „Zaloguj się" i CTA sukcesu tej rejestracji. Gradient
              bursztyn → zieleń z białym tekstem miał 2,2–3,3:1, a pod
              przezroczystą ramką `.btn` powtarzał się na krawędziach
              jednopikselowym pasem koloru przeciwnego końca. */}
          <div className="flex gap-4 mt-8">
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="btn btn--secondary flex-1"
              >
                Wstecz
              </button>
            )}
            {step < 3 ? (
              <button type="button" onClick={handleNext} className="btn btn--primary flex-1">
                Dalej
              </button>
            ) : (
              <button type="submit" disabled={isLoading} className="btn btn--primary flex-1">
                {isLoading ? (
                  <span className="spinner" />
                ) : (
                  <>
                    <UserPlus className="w-5 h-5" />
                    Wyślij zgłoszenie
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-aurora-muted text-sm">
            Masz już konto?{' '}
            <Link to="/login" className={`${LINK_PARTNERA} font-medium`}>
              Zaloguj się
            </Link>
          </p>
          <p className="text-aurora-subtle text-xs mt-2">
            <Link to="/register" className="hover:text-aurora-muted">
              Standardowa rejestracja (dla klientów)
            </Link>
          </p>
        </div>
      </div>
    </UkladWejscia>
  );
}
